import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useRecentlyViewedProductsQuery } from '@src/queries/RecentlyViewedProductsQuery.generated'
import { AnalyticsDispType } from '@src/types/graphql-types'
import { makeStyles } from 'tss-react/mui'
import DynamicProductCarousel from '@src/components/DynamicProductCarousel'
import { CarouselProductTypes } from '../DynamicProductCarousel/DynamicProductCardTypes'
import { sendEmptyRecommendationGA4Event } from '@utils/analytics'
import { ErpType } from '@utils/useProductErpType'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useRouter } from '@src/routes'
import { useCategorizePage } from '@src/utils/analytics/CategorizePage'
import { EventValues } from '@sial/common-utils'

const useStyles = makeStyles()((theme) => {
  return {
    recentlyViewedContainer: {
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      paddingTop: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        paddingBottom: theme.spacing(14),
      },
    },
  }
})

interface RecentlyViewedProductsCarouselProps {
  dispType: AnalyticsDispType
  className?: string
  productKey?: string
  brand?: string
  searchKey?: string
  analyticsItemListingId?: string | null
  analyticsItemListingName?: string | null
  slidesToShow?: number
  setShouldDisplay?: Dispatch<SetStateAction<boolean>>
  condensed?: boolean
  productNumber?: string
}

const RecentlyViewedProductsCarousel: React.FC<
  RecentlyViewedProductsCarouselProps
> = ({
  dispType,
  className,
  productKey = '',
  brand = '',
  searchKey = '',
  analyticsItemListingId,
  analyticsItemListingName,
  slidesToShow,
  setShouldDisplay,
  condensed = false,
  productNumber,
}) => {
  const { classes, cx } = useStyles()
  const router = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const pageType = useCategorizePage()

  const getGAEmptyRecoDetail = (): string => {
    switch (pageType.toLowerCase()) {
      case 'pdp':
        return productKey.toLowerCase()
      case 'srp':
        return searchKey.toLowerCase()
      case 'document search':
        return router.query.tab || 'sds' // the default tab for the doc search page
      default:
        return EventValues.NotAvailable
    }
  }

  const { isBlueB2BUser } = useCurrentUser()
  const { data, loading } = useRecentlyViewedProductsQuery({
    variables: {
      dispType,
      productKey,
      brand,
      ...(isBlueB2BUser ? { erpType: ErpType.Blue } : {}),
    },
    ssr: false,
    fetchPolicy: 'no-cache',
    skip: !isMounted,
    onCompleted: (loadedData) => {
      if (
        getGAEmptyRecoDetail() !== EventValues.NotAvailable &&
        (!loadedData ||
          loadedData?.getRecentlyViewedProducts?.products.length === 0)
      ) {
        sendEmptyRecommendationGA4Event({
          action: pageType,
          detail: getGAEmptyRecoDetail(),
          section: 'recently viewed products',
          productId: productNumber?.toLowerCase(),
          productBrand: brand.toLowerCase(),
        })
      }
    },
  })

  const recentlyViewProducts = data?.getRecentlyViewedProducts?.products || []
  const shouldRender = recentlyViewProducts.length > 0

  useEffect(() => {
    if (setShouldDisplay) {
      setShouldDisplay(shouldRender)
    }
  }, [shouldRender, loading])

  // Return null if no recently viewed products are found
  if (loading) return null

  return shouldRender ? (
    <div className={cx(className, classes.recentlyViewedContainer)}>
      <DynamicProductCarousel
        type={CarouselProductTypes.RecentlyViewed}
        products={recentlyViewProducts}
        analyticsItemListingId={analyticsItemListingId}
        analyticsItemListingName={analyticsItemListingName}
        slidesToShow={slidesToShow}
        condensed={condensed}
      />
    </div>
  ) : null
}

export default RecentlyViewedProductsCarousel
